import { LoadingState } from "@repo/app-types/client/client";
import { Color } from "@repo/app-types/constants/colors";
import type {
  BalanceTransaction,
  BalanceTransactionStatus,
} from "@repo/app-types/entities/balanceTransaction";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import { format } from "date-fns";
import type { FC } from "react";
import { PaginationControls } from "../../miscellaneous/paginationControls.js";
import { UnitAmountText } from "./unitAmountText.js";

interface GiftCardBalanceTableProps {
  balanceTransactions: BalanceTransaction[];
  loadingState: LoadingState;
  onClickNextPage: () => void;
  onClickPreviousPage: () => void;
  page: number;
  totalPages: number;
}

const transactionStatusToColor: Record<BalanceTransactionStatus, PillColor> = {
  confirmed: Color.Green,
  failed: Color.Red,
  pending: Color.Yellow,
};

export const GiftCardBalanceTable: FC<GiftCardBalanceTableProps> = ({
  balanceTransactions,
  loadingState,
  onClickNextPage,
  onClickPreviousPage,
  page,
  totalPages,
}) => {
  const renderTableRows = () => {
    if (loadingState !== LoadingState.LOADED) {
      return <TableSkeleton />;
    }

    if (balanceTransactions.length === 0) {
      return (
        <tr>
          <td
            className="px-3 py-9 text-center text-sm font-semibold text-foreground"
            colSpan={4}
          >
            No gift card activity yet
          </td>
        </tr>
      );
    }

    return (
      <>
        {balanceTransactions.map((transaction) => (
          <tr key={transaction.id}>
            <td className="overflow-hidden py-4 pr-3 pl-4 text-sm font-medium text-ellipsis text-foreground sm:pl-6">
              {transaction.description}
            </td>
            <td className="overflow-hidden px-3 py-4 text-sm text-ellipsis text-muted-foreground">
              <UnitAmountText unitAmount={transaction.unit_amount} />
            </td>
            <td className="overflow-hidden px-3 py-4 text-sm text-ellipsis text-muted-foreground">
              <Pill
                label={capitalize(transaction.status)}
                size={Size.SMALL}
                color={transactionStatusToColor[transaction.status]}
              />
            </td>
            <td className="overflow-hidden px-3 py-4 text-sm text-ellipsis whitespace-nowrap text-muted-foreground">
              {format(new Date(transaction.created_at), "MMM d, yyyy")}
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <>
      <div className="overflow-hidden shadow-sm ring-1 ring-black/5 sm:rounded-lg">
        <table className="w-full min-w-full table-fixed divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="w-[20%] overflow-hidden px-3 py-3.5 text-left text-sm font-semibold text-ellipsis text-foreground"
                scope="col"
              >
                Activity
              </th>
              <th
                className="w-[15%] overflow-hidden px-3 py-3.5 text-left text-sm font-semibold text-ellipsis text-foreground"
                scope="col"
              >
                Amount
              </th>
              <th
                className="w-[15%] overflow-hidden px-3 py-3.5 text-left text-sm font-semibold text-ellipsis text-foreground"
                scope="col"
              >
                Status
              </th>
              <th
                className="w-[20%] overflow-hidden px-3 py-3.5 text-left text-sm font-semibold text-ellipsis text-foreground"
                scope="col"
              >
                Date
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-light bg-white">
            {renderTableRows()}
          </tbody>
        </table>
      </div>
      <div className="pt-2">
        <PaginationControls
          page={page}
          totalPages={totalPages}
          onClickNextPage={onClickNextPage}
          onClickPreviousPage={onClickPreviousPage}
        />
      </div>
    </>
  );
};
